import { forwardRef, ReactNode } from 'react'
import { useTextField } from '@react-aria/textfield'
import { useObjectRef } from 'utils/useObjectRef'
import { useField } from 'formik'
import cx from 'classnames'
import Styles from './TextField.module.scss'
import { FieldHookConfig } from 'formik/dist/Field'
import { TextFieldProps, TextFieldSize, TextFieldWithCounterProps, useTextFieldCounter } from './useTextFieldCounter'
import { useScrollToError } from './ScrollToError'

export const TextInputField = forwardRef<HTMLInputElement, TextFieldWithCounterProps<HTMLInputElement>>(
  (props, forwardedRef) => {
    const {
      onChange,
      size = 'md',
      frontIcon,
      labelClassName,
      errorMessage,
      doneOnEnter = false,
      ...passedProps
    } = props
    const ref = useObjectRef<HTMLInputElement>(forwardedRef)
    const { labelProps, inputProps, errorMessageProps, isInvalid, validationErrors, validationDetails } = useTextField(
      passedProps,
      ref,
    )

    // the event is swallowed by the onChange handler, we want to expose this
    const { onChange: ariaOnChange, ...ariaInputProps } = inputProps

    let errorMessageString: ReactNode = null
    if (typeof errorMessage === 'function') {
      errorMessageString =
        isInvalid && validationErrors != null && validationDetails != null
          ? errorMessage({
              isInvalid: true,
              validationErrors,
              validationDetails,
            })
          : null
    } else {
      errorMessageString = errorMessage
    }

    return (
      <div className={cx(Styles.textField, props.className, Styles[size])}>
        {props.label && (
          <label className={cx(Styles.label, labelClassName)} {...labelProps}>
            {props.label} {props.counter && <span>{props.counter}</span>}
          </label>
        )}
        <div
          className={cx(Styles.inputWrapper, props.wrapperClassName, {
            [Styles.error]: Boolean(errorMessageString),
            [Styles.withFrontIcon]: !!frontIcon,
          })}
        >
          {frontIcon}
          <input
            className={cx(Styles.input, props.inputClassName)}
            {...ariaInputProps}
            onChange={onChange}
            enterKeyHint={doneOnEnter ? 'done' : undefined}
            onKeyDown={(e) => {
              if (doneOnEnter && e.key === 'Enter') {
                e.preventDefault()
                e.currentTarget.blur()
              } else {
                ariaInputProps.onKeyDown?.(e)
              }
            }}
            ref={ref}
          />
        </div>
        {errorMessageString && (
          <div {...errorMessageProps} className={cx(Styles.errorMessage, props.errorCustomClassName)}>
            {errorMessageString}
          </div>
        )}
        {props.validMessage && <div className={Styles.validMessage}>{props.validMessage}</div>}
      </div>
    )
  },
)

export const TextInputFieldWithCounter = forwardRef<HTMLInputElement, TextFieldProps<HTMLInputElement>>(
  (props, ref) => {
    const fieldProps = useTextFieldCounter(props)
    return <TextInputField {...fieldProps} ref={ref} />
  },
)

interface FormikTextFieldProps {
  withCounter?: boolean
  label?: string
  size?: TextFieldSize
  wrapperClassName?: string
}

export const FormikTextInputField = ({
  withCounter = false,
  ...props
}: FieldHookConfig<string> & FormikTextFieldProps) => {
  const [field, meta] = useField(props)
  const { inputRef } = useScrollToError(props.name)
  const displayError = meta.touched && meta.error
  const enhancedProps = {
    ...props,
    ...field,
    ...(displayError && { errorMessage: meta.error }),
  }
  const Component = withCounter ? TextInputFieldWithCounter : TextInputField
  // @ts-ignore
  return <Component ref={inputRef} {...enhancedProps} />
}
